<template>
    <div
        v-if="data && data.length"
        class="uik-bars"
        :class="`uik-bars--${size} uik-bars--back-${backBars}`"
    >
        <component
            :is="item.tooltip ? 'UikTooltip' : 'div'"
            v-for="(item, index) in data"
            :key="'bar-' + index"
            class="uik-bars__bar-wrapper"
            :class="`uik-bars__bar-wrapper--${color}`"
            :text="item.tooltip || null"
        >
            <div v-if="backBars !== 'none'" class="uik-bars__bar"/>
            <div
                class="uik-bars__bar-fill"
                :class="{'uik-bars__bar-fill--visible': getItemHeight(item)}"
                :style="`height: ${getItemHeight(item)}%`"
            />
        </component>
    </div>
</template>

<script>
export default {
    name: "UikBars",
    props: {
        /**
         * Bars data
         *
         * Can be array of strings, numbers or objects with 'data' property.
         *
         * Examples:
         * - [200, "100", 300]
         * - [ { data: 200 }, { data: "100" }, { data: 300 } ]
         */
        data: {
            type: Array,
            default () { return [] }
        },
        /**
         * Max value that represents 100%
         * When not defined, the highest value represents 100%
         */
        max: {
            type: [String, Number],
            default: ""
        },
        /**
         * Bars color
         */
        color: {
            type: String,
            default: "primary",
            validator (value) {
                return [
                    "primary",
                    "secondary",
                    "success",
                    "warning",
                    "error",
                    "red",
                    "orange",
                    "yellow",
                    "lime",
                    "green",
                    "cyan",
                    "blue",
                    "purple",
                    "pink",
                    "gray"
                ].indexOf(value) !== -1
            }
        },
        /**
         * Back bars visualization
         */
        backBars: {
            type: String,
            default: "gray",
            validator (value) {
                return [
                    "gray",
                    "colored",
                    "none"
                ]
            }
        },
        /**
         * Component size
         */
        size: {
            type: String,
            default: "regular",
            validator (value) {
                return [
                    "big",
                    "regular",
                    "small"
                ].indexOf(value) !== -1
            }
        }
    },
    methods: {
        /**
         * Gets item's height
         *
         * @returns {Number} height in percentage
         */
        getItemHeight (item) {
            const max = this.getHighestValue()
            const value = this.getItemValue(item)

            return value * 100 / max
        },
        /**
         * Gets highest value from data
         *
         * @returns {Number}
         */
        getHighestValue () {
            const max = this.getItemValue(this.max)
            if (max) return max

            let highest = 0

            for (let i = 0; i < this.data.length; i++) {
                const value = this.getItemValue(this.data[i])
                if (value > highest) highest = value
            }

            return highest
        },
        /**
         * Gets item's value
         *
         * @returns {Number}
         */
        getItemValue (item) {
            let value = item
            if (typeof item === "object") value = item.data

            value = parseFloat(value)
            if (isNaN(value)) return 0

            return value
        }
    }
}
</script>

<style lang="scss">
.uik-bars {
    display: flex;
    flex-flow: row nowrap;
    justify-content: center;
    align-items: stretch;

    .uik-bars__bar-wrapper {
        height: 100%;
        display: flex;
        flex-flow: column-reverse nowrap;
        justify-content: flex-start;
        align-items: center;
        position: relative;

        .uik-bars__bar {
            height: 100%;
            border-radius: 99px;
            position: relative;
            z-index: 1;

            &:hover {
                transition: all 0.1s, background-color 0s;
            }
        }

        .uik-bars__bar-fill {
            position: absolute;
            bottom: 0;
            z-index: 2;
            height: 50%;
            border-radius: 99px;
            transition: all 0.1s;

            &:hover {
                transition: all 0.1s, background-color 0s;
            }

            &--visible {
                min-height: 5px;
            }
        }

        .uik-tooltip__tooltip-container {
            top: 0;
            position: absolute;
        }

        @each $name, $color in $colors {
            &--#{"" + $name} {
                .uik-bars__bar-fill {
                    background: $color;
                }

                &:hover {
                    .uik-bars__bar {
                        background: rgba($color, 0.4);
                    }

                    .uik-bars__bar-fill {
                        background: lighten($color, 10%)
                    }
                }
            }
        }

        @each $name, $color in $projectColors {
            &--#{"" + $name} {
                .uik-bars__bar-fill {
                    background: $color;
                }

                &:hover {
                    .uik-bars__bar {
                        background: rgba($color, 0.4);
                    }

                    .uik-bars__bar-fill {
                        background: lighten($color, 10%)
                    }
                }
            }
        }
    }

    &--regular {
        height: 43px;

        .uik-bars__bar-wrapper {
            $width: 5px;
            width: calc(#{$width} + 6px);
            padding: 0 3px;

            .uik-bars__bar {
                width: calc(#{$width} - 2px);
            }

            .uik-bars__bar-fill {
                width: $width;
                left: 3px;
            }
        }
    }

    &--big {
        height: 50px;

        .uik-bars__bar-wrapper {
            $width: 6px;
            width: calc(#{$width} + 6px);
            padding: 0 3px;

            .uik-bars__bar {
                width: calc(#{$width} - 2px);
            }

            .uik-bars__bar-fill {
                width: $width;
                left: 3px;
            }
        }
    }

    &--small {
        height: 32px;

        .uik-bars__bar-wrapper {
            $width: 5px;
            width: calc(#{$width} + 4px);
            padding: 0 2px;

            .uik-bars__bar {
                width: calc(#{$width} - 2px);
            }

            .uik-bars__bar-fill {
                width: $width;
                left: 2px;
            }
        }
    }

    &--back {
        &-gray {
            .uik-bars__bar-wrapper {
                .uik-bars__bar {
                    background: darken($borderColor, 4%);
                }
            }
        }

        &-colored {
            .uik-bars__bar-wrapper {
                @each $name, $color in $colors {
                    &--#{"" + $name} {
                        .uik-bars__bar {
                            background: rgba($color, 0.4);
                        }
                    }
                }
            }
        }
    }
}
</style>
